import { Routes } from '@angular/router';
import { canActivateLegacyGuard } from '@core/legacy/legacy.guard';
import { canMatchAuthGuard } from '@core/authentication/auth.guard';
import { canActivateHasPermissionGuard } from '@core/permissions/has-permission.guard';
import { MasterSecureResourceId } from '@gandalf/constants';
import { APP_ROUTING_CONSTANTS } from './app-routing.constants';
import { canDeactivateLockScreenGuard } from './lock/lock-screen.guard';

export const APP_ROUTES: Routes = [
	{
		path: '',
		redirectTo: 'home',
		pathMatch: 'full',
	},
	{
		path: 'lock',
		loadChildren: () => import('./lock/lock.routes').then(m => m.LOCK_ROUTES),
		canMatch: [canMatchAuthGuard],
		canDeactivate: [canDeactivateLockScreenGuard],
		pathMatch: 'full',
	},
	{
		path: 'agreements',
		loadChildren: () => import('./agreements/agreements.routes').then(m => m.AGREEMENTS_ROUTES),
		canMatch: [canMatchAuthGuard],
	},
	{
		path: 'legacy',
		loadChildren: () => import('./legacy/legacy.routes').then(m => m.LEGACY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.LEGACY},
	},
	{
		path: 'accounting/invoices',
		loadChildren: () => import('./accounting/invoices/invoices.routes').then(m => m.INVOICES_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.ACCOUNTING.INVOICES},
	},
	{
		path: 'accounting/claims',
		loadChildren: () => import('./accounting/claims/claims.routes').then(m => m.CLAIMS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.ACCOUNTING.CLAIMS},
	},
	{
		path: 'accounting/payments',
		loadChildren: () => import('./accounting/payments/payments.routes').then(m => m.PAYMENTS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.ACCOUNTING.PAYMENTS},
	},
	{
		path: 'admin',
		loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.ADMIN},
	},
	{
		path: 'connect',
		redirectTo: 'connect/connectDashboard',
		pathMatch: 'full',
	},
	{
		path: 'connect/connectDashboard',
		loadComponent: () => import('./connect/connect.component').then(m => m.ConnectComponent),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.CONNECT.DASHBOARD},
		children: [
			{
				path: 'dashboard',
				loadComponent: () => import('./connect/dashboard/connect-dashboard.component').then(m => m.ConnectDashboardComponent),
				canActivate: [canActivateHasPermissionGuard],
				data: {
					permissions: [MasterSecureResourceId.REVCONNECT_VIEW_DASHBOARD.value],
					shouldReuse: true,
				},
			},
			{
				path: 'not-eligible',
				loadComponent: () => import('./connect/not-eligible/connect-not-eligible.component').then(m => m.ConnectNotEligibleComponent),
				canActivate: [canActivateHasPermissionGuard],
				data: {
					permissions: [MasterSecureResourceId.REVCONNECT_VIEW_DASHBOARD.value],
					shouldReuse: true,
				},
			},
			{
				path: 'settings',
				loadComponent: () => import('./connect/settings/connect-settings.component').then(m => m.ConnectSettingsComponent),
				canActivate: [canActivateHasPermissionGuard],
				data: {
					permissions: [MasterSecureResourceId.REVCONNECT_VIEW_SETTINGS.value],
				},
			},
		],
	},
	{
		path: 'connect/connectCampaigns',
		loadChildren: () => import('./connect/campaign/connect-campaign.routes').then(m => m.CONNECT_CAMPAIGN_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.CONNECT.CAMPAIGN},
	},
	{
		path: 'connect/connectTwoWayText',
		loadChildren: () => import('./connect/two-way-text/two-way-text.routes').then(m => m.TWO_WAY_TEXT_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.CONNECT.TWO_WAY_TEXT},
	},
	{
		path: 'home',
		loadChildren: () => import('./home/home.routes').then(m => m.HOME_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.HOME},
	},
	{
		path: 'inventory/products',
		loadChildren: () => import('./inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.PRODUCTS},
	},
	{
		path: 'inventory/services',
		loadChildren: () => import('./inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.SERVICES},
	},
	{
		path: 'inventory/transfers',
		loadChildren: () => import('./inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.TRANSFERS},
	},
	{
		path: 'inventory/reconciliation',
		loadChildren: () => import('./inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.RECONCILIATION},
	},
	{
		path: 'inventory/packages',
		loadChildren: () => import('./inventory/order-packages/order-packages.routes').then(m => m.ORDER_PACKAGES_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.PACKAGES},
	},
	{
		path: 'inventory/vsp',
		loadChildren: () => import('./inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.INVENTORY.VSP},
	},
	{
		path: 'marketing',
		loadChildren: () => import('./reports/reports.routes').then(m => m.REPORTS_ROUTES),
		canMatch: [canMatchAuthGuard],
	},
	{
		path: 'messaging',
		loadChildren: () => import('./messaging/messaging.routes').then(m => m.MESSAGING_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.MESSAGING},
	},
	{
		path: 'orders',
		redirectTo: 'orders/ordersEnhanced',
		pathMatch: 'full',
	},
	{
		path: 'orders/ordersEnhanced',
		loadChildren: () => import('./orders/orders.routes').then(m => m.ORDERS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.ORDERS},
	},
	{
		path: 'orders/ordersLegacy',
		loadChildren: () => import('./orders/orders.routes').then(m => m.ORDERS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.LEGACY_ORDERS},
	},
	{
		path: 'patients',
		loadChildren: () => import('./patients/patients.routes').then(m => m.PATIENTS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.PATIENTS},
	},
	{
		path: 'schedule',
		loadChildren: () => import('./schedule/schedule.routes').then(m => m.SCHEDULE_ROUTES),
		canMatch: [canMatchAuthGuard],
	},
	{
		path: 'tasks',
		loadChildren: () => import('./tasks/tasks.routes').then(m => m.TASKS_ROUTES),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {key: APP_ROUTING_CONSTANTS.TASKS},
	},
	{
		path: 'revEngage',
		loadComponent: () => import('./revengage/rev-engage.component').then(m => m.RevEngageComponent),
		canMatch: [canMatchAuthGuard],
		canActivate: [canActivateLegacyGuard],
		data: {
			key: APP_ROUTING_CONSTANTS.REV_ENGAGE,
			shouldReuse: true,
		},
	},
];
